import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { DateTime, Duration } from "luxon";
import { PauseIcon, TimerIcon } from "lucide-react";
import {
  type ConversationStatus,
  SlaState,
} from "@lytra/frontend-common/__generated__/graphql";
import {
  Badge,
  type BadgeTone,
} from "@lytra/frontend-common/components/v2/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@lytra/frontend-common/components/v2/ui/tooltip";
import { useNow } from "@/hooks/useNow";
import { statusLabel } from "./ticketHelpers";

/** The SLA fields a badge reads; every ticket query selects these. */
export interface TicketSlaView {
  policyName: string;
  state: SlaState;
  responseDueAt: string;
  firstResponseAt?: string | null;
  resolutionDueAt?: string | null;
  resolvedAt?: string | null;
  pausedSince?: string | null;
  pauseReason?: ConversationStatus | null;
  nextDueAt?: string | null;
}

/** The badge tone per SLA state: the one colour mapping every SLA view uses. */
export const SLA_TONE: Record<SlaState, BadgeTone> = {
  [SlaState.Ok]: "neutral",
  [SlaState.AtRisk]: "warning",
  [SlaState.Breached]: "error",
};

// The sweep refreshes the state every few minutes; the countdown runs in between.
const TICK_MS = 30_000;

/** "2 Std. 10 Min.", "3 T. 4 Std.", "12 Min.": two units at most. */
export function formatShortDuration(language: string, ms: number): string {
  const total = Math.max(1, Math.round(Math.abs(ms) / 60_000));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;
  const parts =
    days > 0 ? { days, hours } : hours > 0 ? { hours, minutes } : { minutes };
  const shown = Object.fromEntries(
    Object.entries(parts).filter(([, value]) => value > 0),
  );
  return Duration.fromObject(shown, { locale: language }).toHuman({
    unitDisplay: "short",
    listStyle: "narrow",
  });
}

const formatAt = (language: string, iso: string) =>
  DateTime.fromISO(iso)
    .setLocale(language)
    .toLocaleString(DateTime.DATETIME_MED);

function slaLabel(
  t: TFunction,
  language: string,
  sla: TicketSlaView,
  now: number,
): string {
  if (sla.pausedSince && sla.pauseReason) {
    return t("sla.paused", { reason: statusLabel(t, sla.pauseReason) });
  }
  if (sla.nextDueAt) {
    const left = DateTime.fromISO(sla.nextDueAt).toMillis() - now;
    const duration = formatShortDuration(language, left);
    return left >= 0
      ? t("sla.left", { duration })
      : t("sla.overdue", { duration });
  }
  return sla.state === SlaState.Breached ? t("sla.breached") : t("sla.met");
}

export default function SlaBadge({
  sla,
  size = "xs",
}: {
  sla: TicketSlaView;
  size?: "xs" | "sm";
}) {
  const { t, i18n } = useTranslation();
  const now = useNow(TICK_MS);
  const overdue =
    sla.nextDueAt != null && DateTime.fromISO(sla.nextDueAt).toMillis() < now;
  // A clock the sweep has not seen run out yet still reads breached the moment it does.
  const tone = overdue
    ? "error"
    : sla.pausedSince
      ? "neutral"
      : SLA_TONE[sla.state];
  const lines = [
    t("sla.policy", { name: sla.policyName }),
    sla.firstResponseAt
      ? t("sla.respondedAt", {
          at: formatAt(i18n.language, sla.firstResponseAt),
        })
      : t("sla.responseDue", {
          at: formatAt(i18n.language, sla.responseDueAt),
        }),
    sla.resolvedAt
      ? t("sla.resolvedAt", { at: formatAt(i18n.language, sla.resolvedAt) })
      : sla.resolutionDueAt
        ? t("sla.resolutionDue", {
            at: formatAt(i18n.language, sla.resolutionDueAt),
          })
        : null,
    sla.pausedSince
      ? t("sla.pausedSince", { at: formatAt(i18n.language, sla.pausedSince) })
      : null,
  ].filter((line): line is string => line !== null);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            size={size}
            tone={tone}
            icon={sla.pausedSince ? PauseIcon : TimerIcon}
            aria-label={`${t("sla.label")}: ${slaLabel(t, i18n.language, sla, now)}`}
          >
            {slaLabel(t, i18n.language, sla, now)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-0.5">
            {lines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
